import {
  IFaxContact,
  IFaxFolder,
  IFaxMessage,
  IFaxTag,
  FaxStatus,
} from '@dataTypes/fax.type'
import { IBlockedUser } from '@dataTypes/blocked-user.type'
import { IContact } from '@dataTypes/contact.type'
import { normalizePhoneNumber } from '@utils/numberFormatter.util'
import moment from 'moment'

export type FaxSystemViewKey =
  | 'inbox'
  | 'sent'
  | 'draft'
  | 'archive'
  | 'trash'
  | 'history'
  | 'blocked'

export type FaxViewKey = FaxSystemViewKey | `folder:${string}` | `tag:${string}`

export type ParsedFaxView =
  | { type: 'system'; key: FaxSystemViewKey }
  | { type: 'folder'; id: string }
  | { type: 'tag'; id: string }

const inCustomFolder = (m: IFaxMessage): boolean =>
  m.fax_folder_id != null && m.fax_folder_id !== ''

export const notInCustomFolder = (m: IFaxMessage): boolean => !inCustomFolder(m)

export const parseFaxViewKey = (activeFolder: string): ParsedFaxView => {
  if (activeFolder.startsWith('folder:')) {
    return { type: 'folder', id: activeFolder.slice('folder:'.length) }
  }
  if (activeFolder.startsWith('tag:')) {
    return { type: 'tag', id: activeFolder.slice('tag:'.length) }
  }
  return { type: 'system', key: activeFolder as FaxSystemViewKey }
}

export const isFaxInboxView = (activeFolder: string): boolean => {
  const parsed = parseFaxViewKey(activeFolder)
  return parsed.type === 'system' && parsed.key === 'inbox'
}

export const isFaxSentView = (activeFolder: string): boolean => {
  const parsed = parseFaxViewKey(activeFolder)
  return parsed.type === 'system' && parsed.key === 'sent'
}

export const properNoun = (word: string): string => {
  if (word.startsWith('folder:') || word.startsWith('tag:')) {
    return word
  }
  return word.charAt(0).toUpperCase() + word.slice(1)
}

export const getFaxViewTitle = (
  activeFolder: string,
  folders: IFaxFolder[],
  tags: IFaxTag[],
): string => {
  const parsed = parseFaxViewKey(activeFolder)
  if (parsed.type === 'folder') {
    return (
      folders.find((f) => f.fax_folder_id === parsed.id)?.folder_name ??
      'Folder'
    )
  }
  if (parsed.type === 'tag') {
    return tags.find((t) => t.fax_tag_id === parsed.id)?.tag_name ?? 'Label'
  }
  return properNoun(parsed.key)
}

export type FaxListViewMode = 'default' | 'history' | 'blocked'

export const getFaxListViewMode = (activeFolder: string): FaxListViewMode => {
  const parsed = parseFaxViewKey(activeFolder)
  if (parsed.type === 'system' && parsed.key === 'history') return 'history'
  if (parsed.type === 'system' && parsed.key === 'blocked') return 'blocked'
  return 'default'
}

export type FaxViewCapabilities = {
  showRefresh: boolean
  showSort: boolean
  showReadToggle: boolean
  showAddContact: boolean
  showDelete: boolean
  showArchiveAction: boolean
  archiveActionIsUnarchive: boolean
  showRecover: boolean
  showMoveToFolder: boolean
  showApplyLabel: boolean
  showToolbarPagination: boolean
}

/**
 * Per-view rules for the fax list toolbar and rows. Centralizes the
 * folder-specific behavior so the toolbar/list components stay declarative.
 */
export const getFaxViewCapabilities = (
  activeFolder: string,
): FaxViewCapabilities => {
  const parsed = parseFaxViewKey(activeFolder)
  const viewMode = getFaxListViewMode(activeFolder)
  const systemKey = parsed.type === 'system' ? parsed.key : null

  const isHistory = viewMode === 'history'
  const isBlocked = viewMode === 'blocked'
  const isDrafts = systemKey === 'draft'
  const isSent = systemKey === 'sent'
  const isArchive = systemKey === 'archive'
  const isTrash = systemKey === 'trash'

  return {
    showRefresh: !isBlocked,
    showSort: !isHistory && !isBlocked,
    showReadToggle: !isDrafts && !isSent && !isBlocked,
    showAddContact: !isDrafts && !isSent,
    showDelete: !isBlocked,
    showArchiveAction: !isTrash && !isBlocked,
    archiveActionIsUnarchive: isArchive,
    showRecover: isTrash,
    showMoveToFolder: !isBlocked && !isTrash,
    showApplyLabel: !isBlocked && !isTrash,
    showToolbarPagination: !isHistory && !isBlocked,
  }
}

const API_FETCHABLE_SYSTEM_VIEWS = new Set<FaxSystemViewKey>([
  'inbox',
  'sent',
  'draft',
  'archive',
  'trash',
  'history',
])

/** View query param for GET /fax/all. Undefined = default mailbox (non-hidden). */
export const getFaxFetchView = (activeFolder: string): string | undefined => {
  const parsed = parseFaxViewKey(activeFolder)
  if (parsed.type !== 'system') return undefined
  if (!API_FETCHABLE_SYSTEM_VIEWS.has(parsed.key)) return undefined
  return parsed.key
}

export const getFaxBlockedUsers = (
  blockedUsers: IBlockedUser[],
): IBlockedUser[] =>
  blockedUsers.filter((user) => {
    if (user.is_hidden) return false
    const features = Array.isArray(user.blocked_features)
      ? user.blocked_features
      : []
    return features.includes('fax')
  })

export const getFaxHistoryRecipient = (
  fax: IFaxMessage,
  clinicName: string,
  counterpartLabel: string,
): string => {
  if (fax.direction === 'Outgoing') return counterpartLabel || '-'
  return clinicName || 'Clinic'
}

export const getFaxHistorySender = (
  fax: IFaxMessage,
  clinicName: string,
  counterpartLabel: string,
): string => {
  if (fax.direction === 'Incoming') return counterpartLabel || '-'
  return clinicName || 'Clinic'
}

export type FaxDisplayStatus = {
  label: string
  tone:
    | 'received'
    | 'delivered'
    | 'sent'
    | 'sending'
    | 'failed'
    | 'draft'
    | 'archived'
    | 'neutral'
}

const TRANSMISSION_STATUSES: FaxStatus[] = [
  'queued',
  'sending',
  'sent',
  'delivered',
  'failed',
]

export const isFaxHistoryEligible = (fax: IFaxMessage): boolean => {
  if (fax.history_eligible != null) return fax.history_eligible
  if (fax.is_hidden || fax.is_archived) return false
  const status = fax.status
  if (!status) return false
  return status !== 'draft'
}

const resolveDisplayStatus = (fax: IFaxMessage): FaxStatus => {
  const status = fax.status
  if (status === 'queued' || status === 'sending') return 'sending'
  if (fax.display_status) return fax.display_status
  if (!status) return 'sent'
  return status
}

export const getFaxDisplayStatus = (fax: IFaxMessage): FaxDisplayStatus => {
  if (fax.is_archived) {
    return { label: 'Archived', tone: 'archived' }
  }
  const status = resolveDisplayStatus(fax)
  switch (status) {
    case 'draft':
      return { label: 'Draft', tone: 'draft' }
    case 'received':
      return { label: 'Received', tone: 'received' }
    case 'delivered':
      return { label: 'Delivered', tone: 'delivered' }
    case 'failed':
      return { label: 'Failed', tone: 'failed' }
    case 'sending':
    case 'queued':
      return { label: 'Sending', tone: 'sending' }
    case 'sent':
      return { label: 'Sent', tone: 'sent' }
    default:
      return { label: 'Sent', tone: 'sent' }
  }
}

export const getFaxTransmissionType = (fax: IFaxMessage): string => {
  if (fax.coversheet_mode === 'uploaded') return 'Manual'
  return 'Digital/eFax'
}

export const formatFaxHistoryTime = (
  dateInput: Date | string | undefined,
): string => {
  if (!dateInput) return ''
  return moment(dateInput).format('h:mm A')
}

export const getBlockedPhoneSet = (
  blockedUsers: IBlockedUser[],
): Set<string> => {
  const phones = new Set<string>()
  for (const user of blockedUsers) {
    if (user.is_hidden) continue
    const features = Array.isArray(user.blocked_features)
      ? user.blocked_features
      : []
    if (!features.includes('fax')) continue
    if (user.phone_number) {
      phones.add(normalizePhoneNumber(user.phone_number))
    }
  }
  return phones
}

const isBlockedFax = (
  m: IFaxMessage,
  blockedPhones: Set<string> | undefined,
): boolean => {
  if (!blockedPhones || blockedPhones.size === 0) return false
  const counterpart = m.direction === 'Incoming' ? m.from_number : m.to_number
  if (!counterpart) return false
  return blockedPhones.has(normalizePhoneNumber(counterpart))
}

export const getFaxMessagesForView = (
  messages: IFaxMessage[],
  viewKey: string,
  options?: { blockedPhones?: Set<string> },
): IFaxMessage[] => {
  const parsed = parseFaxViewKey(viewKey)

  if (parsed.type === 'folder') {
    return messages.filter(
      (f) =>
        f.fax_folder_id === parsed.id && !f.is_hidden && f.status !== 'draft',
    )
  }

  if (parsed.type === 'tag') {
    return messages.filter(
      (f) => f.fax_tag_id === parsed.id && !f.is_hidden && f.status !== 'draft',
    )
  }

  switch (parsed.key) {
    case 'inbox':
      return messages.filter(
        (f) =>
          f.direction === 'Incoming' &&
          f.status === 'received' &&
          !f.is_hidden &&
          !f.is_archived &&
          notInCustomFolder(f),
      )
    case 'sent':
      return messages.filter(
        (f) =>
          f.direction === 'Outgoing' &&
          f.status != null &&
          TRANSMISSION_STATUSES.includes(f.status) &&
          !f.is_hidden &&
          !f.is_archived &&
          notInCustomFolder(f),
      )
    case 'draft':
      return messages.filter(
        (f) => f.status === 'draft' && !f.is_hidden && !f.is_archived,
      )
    case 'archive':
      return messages.filter((f) => f.is_archived && !f.is_hidden)
    case 'trash':
      return messages.filter((f) => f.is_hidden)
    case 'history':
      return messages.filter(isFaxHistoryEligible)
    case 'blocked':
      return messages.filter(
        (f) => !f.is_hidden && isBlockedFax(f, options?.blockedPhones),
      )
    default:
      return []
  }
}

export const belongsToFaxView = (
  message: IFaxMessage,
  viewKey: string,
): boolean => getFaxMessagesForView([message], viewKey).length > 0

/** Merge a view-scoped API fetch into the cached mailbox without dropping other views. */
export const mergeFaxViewData = (
  existing: IFaxMessage[],
  incoming: IFaxMessage[],
  viewKey: string,
): IFaxMessage[] => {
  const byId = new Map(
    existing
      .filter((f) => f.fax_message_id && !belongsToFaxView(f, viewKey))
      .map((f) => [f.fax_message_id!, f]),
  )
  for (const message of incoming) {
    if (message.fax_message_id) {
      byId.set(message.fax_message_id, message)
    }
  }
  return Array.from(byId.values())
}

export const partitionFaxMessages = (
  fax: IFaxMessage[],
): {
  inbox: IFaxMessage[]
  sent: IFaxMessage[]
  archive: IFaxMessage[]
  draft: IFaxMessage[]
  trash: IFaxMessage[]
  history: IFaxMessage[]
} => ({
  inbox: getFaxMessagesForView(fax, 'inbox'),
  sent: getFaxMessagesForView(fax, 'sent'),
  archive: getFaxMessagesForView(fax, 'archive'),
  draft: getFaxMessagesForView(fax, 'draft'),
  trash: getFaxMessagesForView(fax, 'trash'),
  history: getFaxMessagesForView(fax, 'history'),
})

/** @deprecated Use partitionFaxMessages */
export const sortByFolder = partitionFaxMessages

export const getFaxFolderCount = (
  messages: IFaxMessage[],
  faxFolderId: string,
): number =>
  messages.filter(
    (f) =>
      f.fax_folder_id === faxFolderId && !f.is_hidden && f.status !== 'draft',
  ).length

export const faxFolderHasUnread = (
  messages: IFaxMessage[],
  faxFolderId: string,
): boolean =>
  messages.some(
    (f) =>
      f.fax_folder_id === faxFolderId &&
      !f.is_hidden &&
      f.status !== 'draft' &&
      !f.is_read,
  )

export const getFaxTagCount = (
  messages: IFaxMessage[],
  faxTagId: string,
): number =>
  messages.filter(
    (f) => f.fax_tag_id === faxTagId && !f.is_hidden && f.status !== 'draft',
  ).length

export const faxTagHasUnread = (
  messages: IFaxMessage[],
  faxTagId: string,
): boolean =>
  messages.some(
    (f) =>
      f.fax_tag_id === faxTagId &&
      !f.is_hidden &&
      f.status !== 'draft' &&
      !f.is_read,
  )

export const getBlockedFaxCount = (
  messages: IFaxMessage[],
  blockedPhones: Set<string>,
): number =>
  getFaxMessagesForView(messages, 'blocked', { blockedPhones }).length

export const getFaxCounterpartNumber = (
  fax: IFaxMessage,
  isInbox: boolean,
): string => {
  if (isInbox || fax.direction === 'Incoming') {
    return fax.from_number ?? ''
  }
  return fax.to_number ?? ''
}

export const getContactFullName = (
  contact?: IFaxContact | IContact | null,
): string => {
  if (!contact) return ''
  return [contact.first_name, contact.last_name]
    .filter(Boolean)
    .join(' ')
    .trim()
}

export const getFaxDisplayLabel = (
  fax: IFaxMessage,
  isInbox: boolean,
): string => {
  const contact = fax.contact
  if (contact?.contact_id) {
    const name = getContactFullName(contact)
    if (name) return name
  }
  const num = getFaxCounterpartNumber(fax, isInbox)
  return num || '-'
}

export const formatFaxListDate = (
  dateInput: Date | string | undefined,
): string => {
  if (!dateInput) return ''
  const date = moment(dateInput)
  const now = moment()
  if (date.isSame(now, 'day')) return date.format('h:mm A')
  if (date.isSame(now.clone().subtract(1, 'day'), 'day')) return 'Yesterday'
  if (date.isAfter(now.clone().subtract(7, 'days'))) {
    return date.format('ddd')
  }
  return date.format('MMM D')
}

export const formatFileSize = (bytes?: number | null): string => {
  if (bytes == null || bytes <= 0) return ''
  const mb = bytes / (1024 * 1024)
  if (mb >= 1) return `${Math.round(mb)}MB`
  const kb = bytes / 1024
  return `${Math.round(kb)}KB`
}

export type FaxFilterConfig = {
  filters: Record<string, string[]>
  filterLabels: Record<string, string>
}

const FAX_FILTER_LABELS: Record<string, string> = {
  Read: 'Read status',
  Status: 'Delivery status',
  Direction: 'Direction',
  Coversheet: 'Coversheet',
  Updated: 'Last updated',
  Attachments: 'Attachments',
  Blocked: 'Date blocked',
}

export const FAX_DATE_RANGE_OPTIONS = [
  'Older than a week',
  'Older than a month',
  'Older than 6 months',
  'Older than a year',
] as const

const dateRangeStart = (label: string): moment.Moment | null => {
  const now = moment()
  switch (label) {
    case 'Older than a week':
      return now.subtract(1, 'week')
    case 'Older than a month':
      return now.subtract(1, 'month')
    case 'Older than 6 months':
      return now.subtract(6, 'months')
    case 'Older than a year':
      return now.subtract(1, 'year')
    default:
      return null
  }
}

const isDateOlderThanAny = (
  dateInput: Date | string | undefined,
  labels: string[],
): boolean => {
  if (!dateInput) return false
  const date = moment(dateInput)
  return labels.some((label) => {
    const threshold = dateRangeStart(label)
    return threshold != null && date.isBefore(threshold)
  })
}

/** Returns the filter categories that make sense for the active view. */
export const getFaxFilterConfig = (activeFolder: string): FaxFilterConfig => {
  const parsed = parseFaxViewKey(activeFolder)
  const filters: Record<string, string[]> = {}

  if (parsed.type === 'folder' || parsed.type === 'tag') {
    filters.Read = ['Read', 'Unread']
    filters.Direction = ['Inbound', 'Outbound']
  } else {
    switch (parsed.key) {
      case 'inbox':
        filters.Read = ['Read', 'Unread']
        break
      case 'sent':
        filters.Status = ['Sending', 'Sent', 'Delivered', 'Failed']
        break
      case 'history':
        filters.Direction = ['Inbound', 'Outbound']
        filters.Status = ['Sending', 'Sent', 'Delivered', 'Failed', 'Received']
        filters.Coversheet = ['Template', 'Uploaded', 'None']
        break
      case 'archive':
      case 'trash':
        filters.Read = ['Read', 'Unread']
        break
      case 'draft':
        filters.Updated = [...FAX_DATE_RANGE_OPTIONS]
        filters.Attachments = ['Has attachments', 'None']
        break
      case 'blocked':
        filters.Blocked = [...FAX_DATE_RANGE_OPTIONS]
        break
      default:
        break
    }
  }

  const filterLabels: Record<string, string> = {}
  for (const key of Object.keys(filters)) {
    filterLabels[key] = FAX_FILTER_LABELS[key] ?? key
  }
  return { filters, filterLabels }
}

const coversheetModeLabel = (fax: IFaxMessage): string => {
  if (fax.coversheet_mode === 'template') return 'Template'
  if (fax.coversheet_mode === 'uploaded') return 'Uploaded'
  return 'None'
}

/** AND across filter categories; OR within a category's selected values. */
export const matchesFaxFilters = (
  fax: IFaxMessage,
  filters: Record<string, string[]>,
): boolean => {
  for (const [category, values] of Object.entries(filters)) {
    if (!values?.length) continue
    if (category === 'Read') {
      const wantRead = values.includes('Read')
      const wantUnread = values.includes('Unread')
      if (wantRead && wantUnread) continue
      if (wantRead && !fax.is_read) return false
      if (wantUnread && fax.is_read) return false
    } else if (category === 'Status') {
      if (!values.includes(getFaxDisplayStatus(fax).label)) return false
    } else if (category === 'Direction') {
      const dir = fax.direction === 'Incoming' ? 'Inbound' : 'Outbound'
      if (!values.includes(dir)) return false
    } else if (category === 'Coversheet') {
      if (!values.includes(coversheetModeLabel(fax))) return false
    } else if (category === 'Updated') {
      const updated = fax.date_time_updated_at ?? fax.date_time_created_at
      if (!isDateOlderThanAny(updated, values)) return false
    } else if (category === 'Attachments') {
      const hasDocs = (fax.documents?.length ?? 0) > 0
      const hasAttachments = values.includes('Has attachments')
      const noAttachments = values.includes('None')
      if (hasAttachments && noAttachments) continue
      if (hasAttachments && !hasDocs) return false
      if (noAttachments && hasDocs) return false
    }
  }
  return true
}

export const matchesBlockedFilters = (
  user: IBlockedUser,
  filters: Record<string, string[]>,
): boolean => {
  for (const [category, values] of Object.entries(filters)) {
    if (!values?.length) continue
    if (category === 'Blocked') {
      if (!isDateOlderThanAny(user.date_time_created_at, values)) return false
    }
  }
  return true
}

/** Matches a blocked user against the free-text search (phone or reason). */
export const matchesBlockedSearch = (
  user: IBlockedUser,
  query: string,
): boolean => {
  const q = query.trim().toLowerCase()
  if (!q) return true
  const phone = (user.phone_number ?? '').toLowerCase()
  const reason = (user.reason ?? '').toLowerCase()
  return (
    phone.includes(q) ||
    normalizePhoneNumber(phone).includes(normalizePhoneNumber(q)) ||
    reason.includes(q)
  )
}

export const getClinicFaxLine = (
  clinicNumbers: {
    contact_methods?: string | null
    is_main?: boolean
    phone_number: string
    line_name?: string | null
  }[],
): (typeof clinicNumbers)[number] | undefined => {
  const faxLines = clinicNumbers.filter((n) => {
    try {
      const methods = JSON.parse(n.contact_methods || '[]') as string[]
      return methods.map((m) => m.toLowerCase()).includes('fax')
    } catch {
      return false
    }
  })
  return faxLines.find((n) => n.is_main) ?? faxLines[0]
}
